/**
 * Semantic Search with LRU Cache
 *
 * Simple semantic search using embeddings with automatic cache management.
 */

import { algo } from 'spherelib-utils'

const { LRUCache } = algo.cache
const sha256 = algo.hash.sha256

/**
 * Calculate cosine similarity between two vectors
 */
function cosineSimilarity(vec1, vec2) {
  if (vec1.length !== vec2.length) {
    throw new Error('Vectors must have the same dimensions')
  }

  let dotProduct = 0
  let magnitude1 = 0
  let magnitude2 = 0

  for (let i = 0; i < vec1.length; i++) {
    dotProduct += vec1[i] * vec2[i]
    magnitude1 += vec1[i] * vec1[i]
    magnitude2 += vec2[i] * vec2[i]
  }

  magnitude1 = Math.sqrt(magnitude1)
  magnitude2 = Math.sqrt(magnitude2)

  if (magnitude1 === 0 || magnitude2 === 0) {
    return 0
  }

  return dotProduct / (magnitude1 * magnitude2)
}

/**
 * Create a semantic search instance
 *
 * @param {Object} config
 * @param {Function} config.embedFn - (texts: string[]) => Promise<number[][]>
 * @param {number} [config.cacheSize=1000] - Max cache size (LRU eviction)
 * @returns {{ search: Function, getCacheStats: Function }}
 *
 * @example
 * const searcher = createSemanticSearch({ embedFn })
 * const results = await searcher.search("query", ["text1", "text2", "text3"])
 * // results: [{ index: 0, text: "text1", score: 0.95 }, ...]
 */
export function createSemanticSearch(config) {
  const { embedFn, cacheSize = 1000 } = config

  if (!embedFn || typeof embedFn !== 'function') {
    throw new Error('embedFn is required')
  }

  const cache = new LRUCache(cacheSize)

  return {
    /**
     * Search for similar strings
     * @param {string} query - Search query
     * @param {string[]} strings - Strings to search through
     * @param {Object} [options]
     * @param {number} [options.limit=5] - Max results
     * @param {number} [options.minSimilarity=0] - Min similarity threshold
     * @returns {Promise<Array<{index: number, text: string, score: number}>>}
     */
    search: async (query, strings, options = {}) => {
      const { limit = 5, minSimilarity = 0 } = options

      if (!strings || strings.length === 0) {
        return []
      }

      // Find strings that need embedding
      const toEmbed = []
      const toEmbedIndices = []

      for (let i = 0; i < strings.length; i++) {
        const hash = sha256(strings[i])
        if (!cache.has(hash)) {
          toEmbed.push(strings[i])
          toEmbedIndices.push(i)
        }
      }

      // Generate missing embeddings
      if (toEmbed.length > 0) {
        const vectors = await embedFn(toEmbed)
        for (let i = 0; i < toEmbed.length; i++) {
          const hash = sha256(toEmbed[i])
          cache.set(hash, vectors[i])
        }
      }

      // Generate query embedding
      const [queryVector] = await embedFn([query])

      // Calculate similarities
      const results = []
      for (let i = 0; i < strings.length; i++) {
        const hash = sha256(strings[i])
        const vector = cache.get(hash)

        if (!vector) continue

        const score = cosineSimilarity(queryVector, vector)
        if (score >= minSimilarity) {
          results.push({ index: i, text: strings[i], score })
        }
      }

      return results
        .sort((a, b) => b.score - a.score)
        .slice(0, limit)
    },

    getCacheStats: () => ({
      size: Object.keys(cache.nodeMap).length,
      maxSize: cache.capacity,
    }),
  }
}

export { cosineSimilarity }
export default createSemanticSearch
